import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSnackbar from "components/SoftSnackbar";
import SoftButton from "components/SoftButton";
import DataTable from "../list/DataTable";
import Icon from "@mui/material/Icon";
import RestoreIcon from "@mui/icons-material/Restore";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { API_URL } from "config";
import { useAuth } from "../../../AuthContext";
import { useModulePermissions } from "utils/modulePermissions";

const TrashedDepartmentList = () => {
  const navigate = useNavigate();
  const { isAuthReady, permissions = [], userPermission = [] } = useAuth();
  const { hasModulePermission, canViewField } = useModulePermissions(
    permissions,
    userPermission,
    "department"
  );

  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });
  const [restoringId, setRestoringId] = useState(null);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  const canViewPage = hasModulePermission("department.pages.trashed.view");
  const canCreate =
    hasModulePermission("department.pages.create.view") &&
    hasModulePermission("department.actions.open_create");
  const canOpenList =
    hasModulePermission("department.pages.list.view") &&
    hasModulePermission("department.actions.open_list");
  const canRestore = hasModulePermission("department.actions.restore");
  const canConfirmRestore = hasModulePermission("department.actions.confirm_restore");
  const canCancelRestore = hasModulePermission("department.actions.cancel_restore");

  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  const fetchDepartments = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_URL}/departments/trashed/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to fetch trashed departments");
      }
      const { data } = await res.json();
      setDepartments(data || []);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Error loading trashed departments",
        color: "error",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthReady) return;
    if (!canViewPage) {
      setLoading(false);
      return;
    }
    fetchDepartments();
  }, [canViewPage, fetchDepartments, isAuthReady]);

  const handleRestoreClick = (department) => {
    if (!canRestore) {
      setSnackbar({
        open: true,
        message: "You do not have permission to restore departments.",
        color: "error",
      });
      return;
    }
    setSelectedDepartment(department);
    setRestoreDialogOpen(true);
  };

  const handleRestoreConfirm = async () => {
    if (!canRestore || !canConfirmRestore) {
      setSnackbar({
        open: true,
        message: "You do not have permission to confirm restore.",
        color: "error",
      });
      return;
    }

    if (!selectedDepartment) return;

    try {
      setRestoringId(selectedDepartment.id);
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_URL}/departments/restore/${selectedDepartment.id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Restore failed");
      }
      const data = await res.json();
      setSnackbar({
        open: true,
        message: data.message || "Department restored successfully",
        color: "success",
      });
      fetchDepartments();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Restore failed",
        color: "error",
      });
    } finally {
      setRestoringId(null);
      setRestoreDialogOpen(false);
      setSelectedDepartment(null);
    }
  };

  const closeRestoreDialog = () => {
    if (!canCancelRestore || restoringId) return;
    setRestoreDialogOpen(false);
    setSelectedDepartment(null);
  };

  const columns = useMemo(() => {
    const visibleColumns = [];
    if (canViewField("id")) visibleColumns.push({ Header: "ID", accessor: "id", align: "left" });
    if (canViewField("name")) visibleColumns.push({ Header: "Name", accessor: "name", align: "left" });
    if (canViewField("deleted_at")) {
      visibleColumns.push({ Header: "Deleted At", accessor: "deleted_at", align: "left" });
    }
    if (canViewField("status")) visibleColumns.push({ Header: "Status", accessor: "status", align: "center" });
    if (canViewField("actions") && canRestore) {
      visibleColumns.push({ Header: "Actions", accessor: "actions", align: "center", disableSortBy: true });
    }
    return visibleColumns;
  }, [canRestore, canViewField]);

  const rows = useMemo(
    () =>
      departments.map((dept) => ({
        id: dept.id,
        name: dept.name,
        deleted_at: dept.deleted_at ? new Date(dept.deleted_at).toLocaleString() : "-",
        status: (
          <SoftBox
            display="inline-flex"
            alignItems="center"
            px={1.5}
            py={0.5}
            borderRadius={1}
            bgcolor={dept.deleted_at || !dept.status ? "error.light" : "success.light"}
          >
            <SoftTypography
              variant="caption"
              fontWeight="bold"
              color={dept.deleted_at || !dept.status ? "error.dark" : "success.dark"}
            >
              {dept.deleted_at || !dept.status ? "INACTIVE" : "ACTIVE"}
            </SoftTypography>
          </SoftBox>
        ),
        actions: (
          <SoftBox display="flex" justifyContent="center">
            {canRestore && (
              <Tooltip title="Restore Department">
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => handleRestoreClick(dept)}
                  aria-label="restore"
                  disabled={restoringId === dept.id}
                >
                  {restoringId === dept.id ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <RestoreIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </SoftBox>
        ),
      })),
    [canRestore, departments, restoringId]
  );

  if (!isAuthReady || (canViewPage && loading)) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox display="flex" justifyContent="center" alignItems="center" height="200px">
          <CircularProgress size={50} color="info" />
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (!canViewPage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="h5" fontWeight="bold">
            Trashed Department List
          </SoftTypography>
          <SoftTypography variant="body2" color="text" mt={1}>
            You do not have permission to view trashed departments.
          </SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Department Management"
        content={snackbar.message}
        open={snackbar.open}
        close={closeSnackbar}
        onClose={closeSnackbar}
        time={3000}
      />

      <SoftBox p={3}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SoftTypography variant="h4" fontWeight="bold">
            Trashed Department List
          </SoftTypography>
          <SoftBox display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
            {canCreate && (
              <SoftButton
                size="small"
                color="info"
                startIcon={<Icon>add</Icon>}
                onClick={() => navigate("/departments/add")}
              >
                Add New Department
              </SoftButton>
            )}
            {canOpenList && (
              <SoftButton
                size="small"
                color="dark"
                variant="outlined"
                startIcon={<Icon>list</Icon>}
                onClick={() => navigate("/departments")}
              >
                Department List
              </SoftButton>
            )}
          </SoftBox>
        </SoftBox>

        {columns.length > 0 ? (
          <DataTable table={{ columns, rows }} isSorted emptyDataMessage="No department found." />
        ) : (
          <SoftBox p={3}>
            <SoftTypography variant="body2" color="text">
              No visible fields are assigned for this page.
            </SoftTypography>
          </SoftBox>
        )}
      </SoftBox>

      <Dialog open={restoreDialogOpen} onClose={closeRestoreDialog}>
        <DialogTitle>Confirm Restore</DialogTitle>
        <DialogContent>
          <SoftTypography variant="body2">
            Are you sure you want to restore the department{" "}
            <strong>{selectedDepartment?.name}</strong>?
          </SoftTypography>
        </DialogContent>
        <DialogActions>
          {canConfirmRestore && (
            <SoftButton
              color="info"
              variant="contained"
              onClick={handleRestoreConfirm}
              disabled={restoringId === selectedDepartment?.id}
              endIcon={restoringId === selectedDepartment?.id ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{
                backgroundColor: "rgb(23, 193, 232)",
                "&:hover": { backgroundColor: "rgba(23, 193, 232, 0.8)" },
                color: "#fff",
                minWidth: 100,
              }}
            >
              {restoringId === selectedDepartment?.id ? "Processing..." : "Confirm"}
            </SoftButton>
          )}
          {canCancelRestore && (
            <SoftButton
              variant="contained"
              onClick={closeRestoreDialog}
              disabled={Boolean(restoringId)}
              sx={{
                backgroundColor: "red",
                "&:hover": { backgroundColor: "darkred" },
                color: "#fff",
                minWidth: 100,
              }}
            >
              Cancel
            </SoftButton>
          )}
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default TrashedDepartmentList;
